import { LoadingOverlay } from "@mantine/core";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { AuthApi, GeneralApi } from "./apis";
import { BasicSectorList } from "./components/basic-sector/list";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Layout } from "./components/layout";
import { SectorList } from "./components/sector/list";
import { SubSectorList } from "./components/sub-sector/list";
import { Access } from "./pages/access";
import Forgot from "./pages/auth/forgot";
import Login from "./pages/auth/login";
import Receipts from "./pages/ebarimt/receipts";
import Homepage from "./pages/homepage";
import NoMatchInSection from "./pages/noMatchInSection";
import { PartnerManagementAdminLists } from "./pages/partner-management/admin";
import { PartnerManagementMerchant } from "./pages/partner-management/merchant";
import { PartnerManagementPartnerLists } from "./pages/partner-management/partner";
import { PartnerManagementPartnerDetail } from "./pages/partner-management/partner/detail";
import { PartnerManagementPartnerEdit } from "./pages/partner-management/partner/edit";
import { PartnerManagementPartnerNew } from "./pages/partner-management/partner/new";
import { Sector } from "./pages/partner-management/sector";
import { PartnerManagementUser } from "./pages/partner-management/user/list";
import { ReferenceB2bList } from "./pages/reference/b2b/list";
import { ReferencePaymentTermList } from "./pages/reference/payment-term/list";
import { ReferenceProductList } from "./pages/reference/product/list";
import { ReferenceProductType } from "./pages/reference/product/type";
import Users from "./pages/user/list";
import Permission from "./pages/user/permission";
import Permissions from "./pages/user/permissions";
import RoleDetail from "./pages/user/role-detail";
import Roles from "./pages/user/roles";
import UserDetail from "./pages/user/user-detail";
import TenantEdit from "./pages/tenant/edit";
import TenantList from "./pages/tenant/list";
import ContractsList from "./pages/billing/contracts/list";
import ContractDetail from "./pages/billing/contracts/detail";
import ContractCreateWizard from "./pages/billing/contracts/create-wizard";
import FeePolicyDraftPage from "./pages/billing/fee-policies/draft";
import FeePolicyDetailPage from "./pages/billing/fee-policies/detail";
import FeePolicyTabsLayout from "./pages/billing/fee-policies/FeePolicyTabsLayout";
import FeePolicyHistoryTab from "./pages/billing/fee-policies/tabs/history";
import FeePolicyPoliciesTab from "./pages/billing/fee-policies/tabs/policies";
import CollectedFeesList from "./pages/billing/collected-fees/list";
import FeePolicyCustomersTab from "./pages/billing/fee-policies/tabs/customers";
import EcChargesList from "./pages/billing/ec-charges/list";
import EcDelinquenciesList from "./pages/billing/ec-delinquencies/list";
import InvoicesList from "./pages/billing/invoices/list";
import InvoiceDetail from "./pages/billing/invoices/detail";
import TenantsList from "./pages/billing/tenants/list";
import TenantBillingDetail from "./pages/billing/tenants/detail";
import { RequirePermission } from "./components/billing/RequirePermission";
import { RequireRole } from "./components/billing/RequireRole";
import { RequireStaff } from "./components/billing/RequireStaff";
import { VerifyPage } from "./pages/verify";
import { useIsTenantAdmin } from "./hooks/useIsTenantAdmin";
import MyBillingDashboard from "./pages/my-billing/dashboard";
import PlatformBilling from "./pages/my-billing/platform";
import MyCollectedFees from "./pages/my-billing/collected-fees";
import CustomerInfoList from "./pages/my-billing/customer-info/list";
import CustomerInfoDetail from "./pages/my-billing/customer-info/detail";
import AccountsList from "./pages/my-billing/accounts";
import SettingsProfile from "./pages/settings/profile";
import BankCallback from "./pages/bank-callback";
import CustomerFeeDetail from "./pages/billing/fee-policies/customer-fee-detail";
import { authMe } from "./store/Auth";
import { initBusinessGeneral } from "./store/BusinessInit";
import { initDehubGeneral } from "./store/DehubInit";
import { initGeneral } from "./store/General";

function FeePolicyDraftRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/my-billing/fee-policy/editor/${id ?? ""}`} replace />;
}

function App() {
  const [loading, setLoading] = React.useState(true);
  // The token whose bootstrap has finished. Rendering the authenticated tree is
  // gated on this matching the current token, NOT just on `loading`. Otherwise,
  // when login dispatches `authChange` the token appears in Redux and App
  // re-renders the authed tree *before* the `init` effect runs to raise
  // `loading` — a partial-state render (user=null, permissions=[]) that a
  // refresh never does (its gate is up from mount). See white-screen-after-login.
  const [bootedToken, setBootedToken] = React.useState<string | null>(null);
  const { accessToken, sessionScope } = useSelector((state: any) => state.auth);
  const isTenantAdmin = useIsTenantAdmin();

  const dispatch = useDispatch();

  const init = React.useCallback(async () => {
    setLoading(true);
    try {
      if (accessToken) {
        // Independent so one 401 doesn't abort the rest: a tenant-admin (CUSTOMER +
        // tid, lgt=PARTNER) is rejected by the STAFF endpoints (de-admin /api/auth/me,
        // /api/general/init) but accepted by the partner/business ones — bootstrap must
        // still complete and render. Real STAFF: all settle fulfilled (unchanged).
        const [auth, resInit, dehubInit, businessInit] = await Promise.allSettled([
          AuthApi.me(),
          GeneralApi.init(),
          GeneralApi.dehubInit(),
          GeneralApi.businessInit(),
        ]);

        if (auth.status === "fulfilled") dispatch(authMe(auth.value));
        if (resInit.status === "fulfilled") dispatch(initGeneral(resInit.value));
        if (dehubInit.status === "fulfilled") dispatch(initDehubGeneral(dehubInit.value));
        if (businessInit.status === "fulfilled") dispatch(initBusinessGeneral(businessInit.value));
      }
    } catch (error: any) {
      console.error("Failed to fetch auth data:", error?.message);
    } finally {
      // Mark the current token as bootstrapped, then drop the loading gate. Both
      // updates are in the same tick so the authed tree only renders once state
      // is fully populated for this token.
      setBootedToken(accessToken ?? null);
      setLoading(false);
    }
  }, [accessToken, dispatch]);

  React.useEffect(() => {
    init();
  }, [init]);

  // A token is present but its bootstrap hasn't completed yet (e.g. the render
  // immediately after login's `authChange`, before `init` re-runs). Hold the
  // loading gate so the authed tree never renders against partial state.
  const booting = !!accessToken && bootedToken !== accessToken;

  if (loading || booting)
    return (
      <div>
        <LoadingOverlay bg={"#adadad"} visible />
      </div>
    );
  else
    return (
      <ErrorBoundary>
      <Routes>
        <Route path="/access" element={<Access />} />
        {accessToken && sessionScope === "AUTHORIZED" ? (
          <>
            <Route index element={isTenantAdmin ? <Navigate to="/my-billing" replace /> : <Homepage />} />
            <Route path="settings" element={<Layout />}>
              <Route path="/settings/profile" element={<RequireRole><SettingsProfile /></RequireRole>} />
            </Route>
            <Route path="/verify" element={<VerifyPage />} />

            <Route path="partner-management" element={<RequireStaff><Layout /></RequireStaff>}>
              <Route path="/partner-management/partner" element={<PartnerManagementPartnerLists />} />
              <Route path="/partner-management/partner/:type/new" element={<PartnerManagementPartnerNew />} />
              <Route path="/partner-management/partner/:type/edit/:id/:step" element={<PartnerManagementPartnerEdit />} />
              <Route path="/partner-management/partner/:type/detail/:id/:step" element={<PartnerManagementPartnerDetail />} />
              <Route path="/partner-management/admin" element={<PartnerManagementAdminLists />} />
              <Route path="/partner-management/user" element={<PartnerManagementUser />} />
              <Route path="/partner-management/sector" element={<Sector />} />
              <Route path="/partner-management/sector/basic" element={<BasicSectorList />} />
              <Route path="/partner-management/sector/sector" element={<SectorList />} />
              <Route path="/partner-management/sector/sub-sector" element={<SubSectorList />} />

              <Route path="/partner-management/merchant" element={<PartnerManagementMerchant />} />
              <Route path="*" element={<NoMatchInSection />} />
            </Route>

            <Route path="reference" element={<RequireStaff><Layout /></RequireStaff>}>
              <Route path="/reference/b2b-list" element={<ReferenceB2bList />} />
              <Route path="/reference/b2b-list/payment-term/:type" element={<ReferencePaymentTermList />} />
              <Route path="/reference/product-list" element={<ReferenceProductList />} />
              <Route path="/reference/product-list/:type" element={<ReferenceProductType />} />
            </Route>

            <Route path="ebarimt" element={<RequireStaff><Layout /></RequireStaff>}>
              <Route path="/ebarimt/receipts" element={<Receipts />} />
            </Route>

            <Route path="users" element={<RequireStaff><Layout /></RequireStaff>}>
              <Route path="/users/role" element={<Roles />} />
              <Route path="/users/role/:id" element={<RoleDetail />} />
              <Route path="/users/permissions" element={<Permissions />} />
              <Route path="/users/permissions/create" element={<Permission />} />
              <Route path="/users/permissions/:id/detail" element={<Permission />} />
              <Route path="/users/permissions/:id/update" element={<Permission />} />
              <Route path="/users/list" element={<Users />} />
              <Route path="/users/list/create" element={<UserDetail />} />
              <Route path="/users/list/:id/detail" element={<UserDetail />} />
              <Route path="/users/list/:id/update" element={<UserDetail />} />
            </Route>

            <Route path="billing" element={<RequireStaff><Layout /></RequireStaff>}>
              <Route path="/billing" element={<RequirePermission code="BIL_001"><TenantsList /></RequirePermission>} />
              <Route path="/billing/tenants" element={<RequirePermission code="BIL_001"><TenantsList /></RequirePermission>} />
              <Route path="/billing/tenants/:tenantId" element={<RequirePermission code="BIL_001"><TenantBillingDetail /></RequirePermission>} />
              <Route path="/billing/contracts" element={<RequirePermission code="BIL_001"><ContractsList /></RequirePermission>} />
              <Route path="/billing/contracts/new" element={<RequirePermission code="BIL_002"><ContractCreateWizard /></RequirePermission>} />
              <Route path="/billing/contracts/:id" element={<RequirePermission code="BIL_001"><ContractDetail /></RequirePermission>} />
              <Route path="/billing/collected-fees" element={<RequirePermission code="BIL_014"><CollectedFeesList /></RequirePermission>} />
              <Route path="/billing/ec-charges" element={<RequirePermission code="BIL_019"><EcChargesList /></RequirePermission>} />
              <Route path="/billing/ec-delinquencies" element={<RequirePermission code="BIL_021"><EcDelinquenciesList /></RequirePermission>} />
              <Route path="/billing/invoices" element={<RequirePermission code="BIL_022"><InvoicesList /></RequirePermission>} />
              <Route path="/billing/invoices/:id" element={<RequirePermission code="BIL_022"><InvoiceDetail /></RequirePermission>} />
              <Route path="*" element={<NoMatchInSection />} />
            </Route>

            <Route path="tenant" element={<RequireStaff><Layout /></RequireStaff>}>
              <Route path="/tenant/list" element={<TenantList />} />
              <Route path="/tenant/:id" element={<TenantEdit />} />
            </Route>

            {/* Bank consent popup landing — no app chrome (Layout) */}
            <Route path="/bank-callback" element={<BankCallback />} />

            <Route path="my-billing" element={<Layout />}>
              <Route path="/my-billing" element={<RequireRole><MyBillingDashboard /></RequireRole>} />

              {/* Платформ төлбөр: single page, 2 tabs (Гэрээ / Нэхэмжлэх) */}
              <Route path="/my-billing/platform" element={<RequireRole><PlatformBilling /></RequireRole>} />

              {/* Back-compat redirects from the old split pages */}
              <Route path="/my-billing/subscription" element={<Navigate to="/my-billing/platform" replace />} />
              <Route path="/my-billing/invoices" element={<Navigate to="/my-billing/platform?tab=invoices" replace />} />

              {/* Tenant Fee Policy: single page, 5 tabs (v1.3 mock IA) */}
              <Route path="/my-billing/fee-policy" element={<RequireRole><FeePolicyTabsLayout /></RequireRole>}>
                <Route index element={<FeePolicyPoliciesTab />} />
                <Route path="customers" element={<FeePolicyCustomersTab />} />
                <Route path="editor" element={<FeePolicyDraftPage />} />
                <Route path="editor/:id" element={<FeePolicyDraftPage />} />
                <Route path="history" element={<FeePolicyHistoryTab />} />
                <Route path="collected" element={<MyCollectedFees />} />
              </Route>

              {/* Phase D — customer fee detail + send fee invoice */}
              <Route path="/my-billing/fee-policy/customers/:id" element={<RequireRole><CustomerFeeDetail /></RequireRole>} />

              {/* Customer info (read-only tenant-admin surface) */}
              <Route path="/my-billing/customer-info" element={<RequireRole><CustomerInfoList /></RequireRole>} />
              <Route path="/my-billing/customer-info/:partnerId" element={<RequireRole><CustomerInfoDetail /></RequireRole>} />

              {/* Policy detail still reachable by direct URL (opens read-only drawer host) */}
              <Route path="/my-billing/fee-policy/:id" element={<RequireRole><FeePolicyDetailPage /></RequireRole>} />

              {/* Back-compat redirects */}
              <Route path="/my-billing/customers" element={<Navigate to="/my-billing/fee-policy/customers" replace />} />
              <Route path="/my-billing/collected-fees" element={<Navigate to="/my-billing/fee-policy/collected" replace />} />
              <Route path="/my-billing/fee-policy/new" element={<Navigate to="/my-billing/fee-policy/editor" replace />} />
              <Route path="/my-billing/fee-policy/:id/draft" element={<FeePolicyDraftRedirect />} />

              {/* Phase A "Данс" — tenant bank accounts */}
              <Route path="/my-billing/accounts" element={<RequireRole><AccountsList /></RequireRole>} />

              <Route path="*" element={<NoMatchInSection />} />
            </Route>

            <Route path="*" element={<Navigate to="/" />} />
          </>
        ) : (
          <>
            <Route path="/auth/login" element={<Login />} />
            <Route path="/auth/forgot" element={<Forgot />} />
            <Route path="*" element={<Navigate to="/auth/login" />} />
          </>
        )}
      </Routes>
      </ErrorBoundary>
    );
}

export default App;
